import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, firstValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { GetProductRequestParams, ProductData } from './products.interface';
import { Router } from '@angular/router';
import { ProductsService } from '../../services/products/products.service';
import { getOffset, showNotification } from 'src/app/shared/helpers/helpers';
import { ModalContent } from '../../shared/modal-content';
import { ModalComponent } from 'angular-custom-modal';
import { OptionItem } from '../../shared/types/api-responses.type';
import { CategoriesService } from '../../services/categories/categories.service';
import { showAlert } from '../../shared/helpers/alert';
import { ProvidersService } from '../../services/providers/providers.service';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../services/users/user.selectors';
import { UserData } from '../../interfaces/users.interface';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.css'],
})
export class ProductsComponent implements OnInit, OnDestroy {
  @ViewChild('modal') modal!: ModalComponent;

  private destroy$ = new Subject<void>();
  loading = false;
  syncing = false;
  exporting = false;
  categories: OptionItem[] = [];
  providers: OptionItem[] = [];
  brands: OptionItem[] = [];
  canViewProviders = false; // Flag untuk membantu menyembunyikan elemen di HTML

  modalData: ModalContent = new ModalContent({
    header: 'Confirmation',
    body: 'Are you sure you want to delete this product?',
    showCloseButton: true,
    showConfirmButton: true,
    closeButtonLabel: 'Cancel',
    confirmButtonLabel: 'Delete',
    closeButtonClass: 'btn-secondary',
    confirmButtonClass: 'btn-danger',
  });

  selectedId = '';
  params = new GetProductRequestParams();
  totalRows = 0;

  cols: any[] = [];

  pageSizeOptions = [5, 10, 25, 50, 100];
  rows: ProductData[] = [];

  userAuth$!: Observable<UserData | null>;
  isAdmin$!: Observable<boolean>;
  isMerchant$!: Observable<boolean>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private productService: ProductsService,
    private readonly categoryService: CategoriesService,
    private readonly providerService: ProvidersService,
    private store: Store<any> // BARU: Inject NgRx Store di sini
  ) {}

  ngOnInit(): void {
    this.getCategoryData().then();
    this.getProducts();
    this.getProviderData().then();
    this.getBrandData().then();
    this.initRoleBasedFetching();

    this.isAdmin$.subscribe((isAdmin) => {
      console.log('isAdmin value:', isAdmin);

      this.cols = [
        { field: 'no', title: 'No' },
        { field: 'category_name', title: 'Category' },
        { field: 'code', title: 'Code' },
        { field: 'name', title: 'Name' },
        { field: 'brand', title: 'Brand' },
        ...(isAdmin ? [{ field: 'price', title: 'Price' }] : []),
        ...(isAdmin ? [{ field: 'selling_price', title: 'Selling Price' }] : []),
        ...(isAdmin ? [{ field: 'provider_code', title: 'Provider' }] : []),

        { field: 'provider_product', title: 'Provider Product' },
        ...(isAdmin ? [{ field: 'admin_fee', title: 'Admin Fee' }] : []),
        ...(isAdmin ? [{ field: 'status_name', title: 'Status' }] : []),

        ...(isAdmin ? [{ field: 'action', title: 'Action', type: 'action' }] : []),
      ];
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Fungsi ini mengatur pengambilan data yang bergantung pada peran.
   */
  private initRoleBasedFetching(): void {
    // const userData$ = this.store.select(selectCurrentUser).pipe(filter((user) => !!user));
    // const isAdmin$ = userData$.pipe(map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false));
    // const isMerchant$ = userData$.pipe(map((user) => user?.roles?.some((role) => role.code === 'merchant') ?? false));

    this.userAuth$ = this.store.select(selectCurrentUser);
    this.isAdmin$ = this.userAuth$.pipe(map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false));
    this.isMerchant$ = this.userAuth$.pipe(map((user) => user?.roles?.some((role) => role.code === 'merchant') ?? false));

    this.userAuth$.subscribe((user) => {
      if (user) {
        this.isAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
          if (isAdmin) {
            // this.getRoleData().then();
            // this.getMerchantData().then();
          } else {
            // this.params.merchantId = user.merchant.id;
          }
        });
        // this.getUsers();
      }
    });
  }

  // combineLatest([isAdmin$, isMerchant$])
  //   .pipe(takeUntil(this.destroy$))
  //   .subscribe(([isAdmin, isMerchant]) => {
  //     // Logika utama: jika pengguna adalah admin ATAU bukan merchant
  //     if (isAdmin || !isMerchant) {
  //       console.log('Akses Provider diizinkan.');
  //       //this.canViewProviders = true;
  //       this.getProviderData(); // Panggil fungsi HANYA jika kondisi terpenuhi
  //     } else {
  //       console.log('Akses Provider ditolak untuk Merchant.');
  //       //this.canViewProviders = false;
  //       this.providers = []; // Kosongkan data provider untuk menyembunyikan dari filter
  //     }
  //     this.cdRef.detectChanges(); // Memicu deteksi perubahan untuk update UI
  //   });
  // }

  private async getCategoryData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.categoryService.getCategories());
      this.categories = data.map((category) => ({ label: category.name, value: category.code }));
    } catch (error) {
      this.showNotification('error', 'Failed to fetch category', JSON.stringify(error));
    }
  }

  private async getBrandData(): Promise<void> {
    try {
      const fetchParams = new GetProductRequestParams();
      fetchParams.rowPerPage = 1000000;
      const { data } = await firstValueFrom(this.productService.getProductService(fetchParams));
      const uniqueBrands = [...new Set(data.map((p) => p.brand).filter((b) => !!b))];
      this.brands = uniqueBrands.sort().map((brand) => ({ label: brand, value: brand }));
    } catch (error) {
      this.showNotification('error', 'Failed to fetch brands', JSON.stringify(error));
    }
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.code }));
    } catch (error) {
      showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  getProducts(): void {
    this.loading = true;
    this.rows = [];

    this.productService
      .getProductService(this.params)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.rows = this.processDataForTable(response.data);
          this.totalRows = response.totalPage;
        },
        error: (err) => console.error('Error fetching product data:', err),
      });
  }

  processDataForTable(data: ProductData[]) {
    return data.map((each, i) => ({
      ...each,
      no: getOffset(this.params.pageNo, this.params.rowPerPage) + i + 1,
    }));
  }

  changeServer(data: any): void {
    this.params.pageNo = data.current_page;
    this.params.rowPerPage = data.pagesize;
    this.params.filter = data.search;
    this.getProducts();
  }

  onInputChanged(value: string): void {
    this.params.filter = '';
    if (value) {
      this.params.filter = value;
    }
    this.cdRef.detectChanges();
  }

  goToForm(id: string): void {
    if (id === 'new') {
      this.router.navigate(['/products/new']).then();
      return;
    }
    this.router.navigate(['/products/edit', id]).then();
  }

  async deleteProduct(id: string): Promise<void> {
    this.loading = true;

    try {
      const delResp = await firstValueFrom(this.productService.deleteProductById(id));
      showNotification('success', 'Success to delete product', JSON.stringify(delResp));
      this.getProducts();
    } catch (error) {
      console.error('Delete Product Error:', error);
    } finally {
      this.loading = false;
    }
  }

  onCategoryChange(categoryOption: OptionItem): void {
    this.params.categoryCode = null;
    if (categoryOption) {
      this.params.categoryCode = categoryOption.value;
    }
    this.getProducts();
  }

  onBrandChange(brandOption: OptionItem): void {
    this.params.brand = null;
    if (brandOption) {
      this.params.brand = brandOption.value;
    }
    this.getProducts();
  }

  onProviderChange(providerOption: OptionItem): void {
    this.params.providerCode = null;
    if (providerOption) {
      this.params.providerCode = providerOption.value;
    }
    this.getProducts();
  }

  async onModalDeleteConfirm(id: string): Promise<void> {
    await this.deleteProduct(id);
    this.modal.close();
  }

  async downloadExcel(): Promise<void> {
    this.exporting = true;
    try {
      const exportParams = new GetProductRequestParams();
      exportParams.rowPerPage = 1000000;
      exportParams.filter = this.params.filter;
      exportParams.categoryCode = this.params.categoryCode;
      exportParams.providerCode = this.params.providerCode;
      exportParams.brand = this.params.brand;

      const response = await firstValueFrom(this.productService.getProductService(exportParams));
      const isAdmin = await firstValueFrom(this.isAdmin$);

      const dataForExcel = response.data.map((item, i) => {
        const row: any = {
          'No': i + 1,
          'Category': item.category_code,
          'Code': item.code,
          'Name': item.name,
          'Brand': item.brand,
        };
        if (isAdmin) {
          row['Price'] = Number(item.price) || 0;
          row['Selling Price'] = Number(item.selling_price) || 0;
          row['Provider'] = item.provider_code;
        }
        row['Provider Product'] = item.provider_product;
        if (isAdmin) {
          row['Admin Fee'] = Number(item.admin_fee) || 0;
          row['Status'] = item.active;
        }
        return row;
      });

      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(dataForExcel);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');

      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(wb, `products_${dateStr}.xlsx`);
    } catch (error) {
      this.showNotification('error', 'Export gagal', (error as Error).message);
    } finally {
      this.exporting = false;
    }
  }

  syncDigiflazz(): void {
    this.syncing = true;
    this.productService.syncDigiflazz()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.syncing = false))
      )
      .subscribe({
        next: () => {
          this.showNotification('success', 'Sync harga berhasil');
          this.getProducts();
        },
        error: (err) => {
          this.showNotification('error', 'Sync harga gagal', err.message);
        },
      });
  }

  private showNotification(type: 'success' | 'error', title: string, text = 'Success'): void {
    showAlert({ type, title, text }).then();
  }
}
